/**
 * ChatBubble — Single chat message bubble.
 *
 * isSent=false (received): glass white bubble, avatar on left.
 * isSent=true  (sent):     pink→lavender gradient bubble, avatar on right.
 *
 * BlurView on iOS for received bubble. RGBA fallback on Android/web.
 * Border radii simulate CSS shorthand:
 *   received: 18 18 18 4 (bottom-left cut)
 *   sent:     18 18 4 18 (bottom-right cut)
 */

import React from 'react';
import { View, Text, StyleSheet, Platform, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { createBoxShadow } from '../../../lib/design-tokens';
import { ClientAvatar } from '../practitioner-dashboard/ClientAvatar';
import type { ChatMessage } from '../../../hooks/usePractitionerClientView';

const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 1, y: 1 } as const;
const SENT_COLORS: [string, string] = ['#EC4899', '#A78BFA'];

const sentBubbleShadow = createBoxShadow({
  color: '#A78BFA',
  offsetX: 0,
  offsetY: 2,
  blurRadius: 8,
  opacity: 0.3,
});

interface ChatBubbleProps {
  message: ChatMessage;
}

export const ChatBubble: React.FC<ChatBubbleProps> = React.memo(function ChatBubble({
  message,
}) {
  const { senderInitials, senderGradientIndex, text, timestamp, isSent } = message;

  const ReceivedBubbleContent = (
    <View style={styles.receivedBubble}>
      <Text style={styles.receivedText}>{text}</Text>
      <Text style={styles.receivedTimestamp}>{timestamp}</Text>
    </View>
  );

  const receivedBubble =
    Platform.OS === 'ios' ? (
      <BlurView intensity={20} tint="light" style={styles.receivedBlur}>
        <View style={styles.receivedBubble}>
          <Text style={styles.receivedText}>{text}</Text>
          <Text style={styles.receivedTimestamp}>{timestamp}</Text>
        </View>
      </BlurView>
    ) : (
      ReceivedBubbleContent
    );

  if (!isSent) {
    return (
      <View style={styles.receivedRow}>
        <ClientAvatar initials={senderInitials} gradientIndex={senderGradientIndex} size={28} />
        {receivedBubble}
      </View>
    );
  }

  return (
    <View style={styles.sentRow}>
      <LinearGradient
        colors={SENT_COLORS}
        start={GRADIENT_START}
        end={GRADIENT_END}
        style={[styles.sentBubble, sentBubbleShadow as ViewStyle]}
      >
        <Text style={styles.sentText}>{text}</Text>
        <Text style={styles.sentTimestamp}>{timestamp}</Text>
      </LinearGradient>
      <ClientAvatar initials={senderInitials} gradientIndex={senderGradientIndex} size={28} />
    </View>
  );
});

const styles = StyleSheet.create({
  receivedRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
  } as ViewStyle,
  sentRow: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-end',
    gap: 8,
  } as ViewStyle,
  receivedBlur: {
    maxWidth: '78%',
    borderRadius: 18,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.4)',
    overflow: 'hidden',
  },
  receivedBubble: {
    maxWidth: '78%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 18,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.4)',
    padding: 10,
    paddingHorizontal: 14,
  },
  receivedText: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 20,
  },
  receivedTimestamp: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 4,
    opacity: 0.6,
  },
  sentBubble: {
    maxWidth: '78%',
    borderRadius: 18,
    borderBottomRightRadius: 4,
    padding: 10,
    paddingHorizontal: 14,
  },
  sentText: {
    fontSize: 14,
    color: '#FFFFFF',
    lineHeight: 20,
  },
  sentTimestamp: {
    fontSize: 11,
    color: '#FFFFFF',
    marginTop: 4,
    opacity: 0.7,
  },
});
